import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import { dateParts } from '@/src/utils/dateFormatter';
import PSText from './ui/PSText';
import { letterSpacing } from '@/constants/tokens';

/**
 * DateSelector — a single selectable day cell (molecule).
 *
 * The fixed-size date card from the event page's date picker: stacked
 * day-of-week / day-number / month, in a bordered box that flips on select.
 *
 * The selected flip changes fill + border COLOR + text color only — the border
 * stays a constant hairline so the cell never changes size between states:
 *   unselected: transparent fill · gray border · black text
 *   selected:   black fill · black border · light text
 * RN text doesn't cascade, so this molecule owns its own text colors off the
 * same `selected` flag (no shared chip abstraction — by design).
 *
 * Takes the slot's RAW datetime and splits it itself (`dateParts`), per the house
 * rule that no component receives a pre-formatted date.
 */
type DateSelectorProps = {
  /** Raw slot ISO datetime, e.g. "2026-07-11T19:30:00". */
  datetime: string;
  selected: boolean;
  onPress?: () => void;
  hasApTickets: boolean;
};

export default function DateSelector({
  datetime,
  selected,
  onPress,
  hasApTickets,
}: DateSelectorProps) {
  const fg = selected ? '$background' : '$color';
  const { weekday, day, month } = dateParts(datetime);

  return (
    <YStack>
      <YStack
        onPress={onPress}
        cursor={onPress ? 'pointer' : undefined}
        minWidth={56}
        p={'$space.sm'}
        rounded="$base"
        items="center"
        justify="center"
        gap="$xs"
        borderWidth="$borderWidth.hairline"
        borderColor={selected ? '$color' : '$borderColor'}
        backgroundColor={selected ? '$color' : '$transparent'}
        borderBottomLeftRadius={hasApTickets ? 0 : '$radius.base'}
      >
        <Text fontFamily="$body" fontSize="$base" color={fg}>
          {weekday}
        </Text>
        <Text
          fontFamily="$body"
          fontWeight="$semibold"
          fontSize="$large"
          color={fg}
        >
          {day}
        </Text>
        <Text fontFamily="$body" fontSize="$base" color={fg}>
          {month}
        </Text>
      </YStack>
      {hasApTickets && (
        <XStack
          backgroundColor={'$color.brand'}
          px={'$space.sm'}
          py={'$space.xs'}
          alignSelf="flex-start"
          borderBottomLeftRadius={'$radius.base'}
          borderBottomRightRadius={'$radius.base'}
          pr={getTokenValue('$space.sm') - letterSpacing.tags}
        >
          <PSText.Tags color={'$color.textWhite'}>AP</PSText.Tags>
        </XStack>
      )}
    </YStack>
  );
}
